// Polish Phase 2 (PARTICLE-02/07, PERF-03, THEME-01, A11Y-PART-01) — the real
// GPU particle render layer.
//
// Plan 08 left this an empty container ("Plan 08 attaches emitters here" —
// never did). It now:
//   - mounts the shared ParticleSystem's render container into the scene
//     (z=4 per World's back-to-front order: above board/letters/floor and the
//     anchor overlay slot — the topmost scene-graph layer),
//   - drives the system's simulation from the Pixi ticker (single-RAF
//     discipline — one ticker.add for the whole particle layer),
//   - keeps the system's theme palette in sync with the active theme so all
//     four themes tint correctly.
//
// The reduced-motion gate lives inside ParticleSystem.emit() (returns 0 /
// spawns nothing), so this layer needs no per-frame motion check — under
// reduced motion the system simply never has live particles to draw.

import { useApplication } from '@pixi/react'
import type {
  Application,
  Container as PixiContainer,
  TickerCallback,
} from 'pixi.js'
import { type JSX, useCallback, useEffect } from 'react'
import { $reducedMotion } from '../../stores/motion'
import { $particleBurst } from '../../stores/particleBus'
import type { BoardView } from '../board-view'
import { useParticleSystem } from '../motion/ParticleSystemContext'
import { paletteFromDescriptor } from '../motion/particle-system'
import type { PlayerView } from '../PlayerViewSchema'
import { useTheme } from '../theme/ThemeProvider'
import {
  boardRows,
  CELL_SIZE_PX,
  CHESS_CELLS,
  cellIdxToScreenX,
  cellIdxToScreenY,
} from './board-layout'

interface EffectLayerProps {
  playerView: PlayerView
  /**
   * ARCA-749 (WS3) — the active view, so a burst resolves its cell center
   * against the right grid geometry. Chess-view bursts (empower / Quill bloom on
   * a piece cell) flip against the FIXED 6 rows; letters-view bursts (coin-spark
   * on a token claim) flip against the growing word field. Defaults to 'letters'
   * for isolation tests.
   */
  boardView?: BoardView
}

export function EffectLayer({
  playerView,
  boardView = 'letters',
}: EffectLayerProps): JSX.Element {
  const system = useParticleSystem()
  const app = useApplication().app
  const { descriptor } = useTheme()

  // Keep the particle palette synced to the active theme (THEME-01).
  useEffect(() => {
    if (!system || !descriptor) return
    system.setPalette(paletteFromDescriptor(descriptor))
  }, [system, descriptor])

  // INT-01 — DOM→Pixi particle bridge. DOM-tree callers push board-cell burst
  // requests onto $particleBurst (coin-spark on a token claim, magic-bloom on
  // empower / Quill target); here — inside the Pixi tree, with the system and
  // the live board layout — we resolve each cell_idx to its on-board center
  // and emit. `.listen` fires on CHANGE only (no immediate replay), so each
  // pushed request emits exactly once. The reduced-motion gate matches the
  // cube emits: under RM we spawn nothing.
  const yourSlot = playerView.your_slot
  // Chess view is the fixed 6×6 (36 cells); letters view tracks the growing
  // word field (board.length). The flip rows below derive from this so a chess
  // burst lands on the same cell PieceLayer drew (which also flips off 6 rows).
  const cellCount =
    boardView === 'chess' ? CHESS_CELLS : playerView.board.length
  useEffect(() => {
    if (!system) return undefined
    const rows = boardRows(cellCount)
    return $particleBurst.listen((req) => {
      if (!req) return
      const reduced = $reducedMotion.get()
      if (reduced) return
      for (const cellIdx of req.cells) {
        const x = cellIdxToScreenX(cellIdx, yourSlot, rows) + CELL_SIZE_PX / 2
        const y = cellIdxToScreenY(cellIdx, yourSlot, rows) + CELL_SIZE_PX / 2
        const params =
          req.count === undefined ? { reduced } : { reduced, count: req.count }
        system.emit(req.kind, x, y, params)
      }
    })
  }, [system, yourSlot, cellCount])

  // Drive the simulation from the renderer ticker (single RAF).
  useEffect(() => {
    if (!system || !app) return undefined
    const cb = (): void => {
      system.update(app.ticker.deltaMS)
    }
    app.ticker.add(cb)
    return () => {
      detachTickerCallback(app, cb)
    }
  }, [system, app])

  // Borrow the system's render container into this layer. On unmount we detach
  // (not destroy) — the provider owns the system's lifecycle/disposal.
  const attach = useCallback(
    (container: PixiContainer | null): void => {
      if (!system) return
      if (container) {
        container.addChild(system.view)
      } else {
        detachBorrowedView(system.view)
      }
    },
    [system],
  )

  return <pixiContainer label="EffectLayer" ref={attach} />
}

// ARCA-135 — detach the borrowed system view from its host container, tolerating
// a parent that Pixi has already torn down. Under React 19 concurrent unmount
// the host pixiContainer (system.view's parent) can be destroyed by the
// @pixi/react reconciler — or the ParticleSystemProvider's dispose() can run —
// BEFORE this ref-null callback fires. The pointer survives, so a bare
// `if (parent)` guard still passes and removeChild() then walks a nulled
// `_next`/`_prev` linked list → `TypeError: reading 'next'`/'remove'. Skip a
// destroyed parent, and wrap removeChild defensively for the reconciler
// destroy-order path where the host is mid-destruction. The orphaned view needs
// no further cleanup here — the provider's dispose() owns the system's teardown.
export function detachBorrowedView(view: PixiContainer): void {
  const parent = view.parent
  if (parent && !parent.destroyed) {
    try {
      parent.removeChild(view)
    } catch {
      // Parent destroyed after the guard read; view is orphaned, dispose() owns it.
    }
  }
}

// ARCA-621 — remove the ticker-driver callback, tolerating a ticker @pixi/react
// has already torn down. This is the SAME destroy-order race detachBorrowedView
// guards for the borrowed-view path above: under React 19 concurrent unmount the
// @pixi/react reconciler runs Application.destroy() — which NULLS `app.ticker` —
// BEFORE this effect's cleanup fires. `app` itself survives (only `.ticker` is
// nulled), so the effect's top-of-body `!app` guard does not catch it; a bare
// `app.ticker.remove(cb)` then dereferences null → `TypeError: reading 'remove'`.
// No-op when the ticker is already gone — the destroyed Application owns its
// ticker's teardown. (`app.ticker` is typed non-null but is null once destroyed.)
export function detachTickerCallback(
  app: Application,
  cb: TickerCallback<unknown>,
): void {
  if (app.ticker) {
    app.ticker.remove(cb)
  }
}
